import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, ArrowLeft } from "lucide-react";
import { formatRoleLabel } from "@/lib/formatRole";
import { supabase } from "@/integrations/supabase/client";


export default function FundraisingTeam() {
  const { ptaId } = useParams();
  const navigate = useNavigate();
  const [members, setMembers] = useState<any[]>([]);
  const [ptaName, setPtaName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeam = async () => {
      if (!ptaId) return;

      // Get PTA info
      const { data: pta } = await supabase.rpc('get_public_pta_info', { p_pta_id: ptaId });
      if (pta && pta.length > 0) {
        setPtaName(pta[0].name);
        setLogoUrl(pta[0].logo_url || "");
      }

      // Get team members
      const { data: teamData } = await supabase.rpc('get_pta_team_members', { p_pta_id: ptaId });
      const hiddenRoles = ['admin', 'custom'];
      const filtered = (teamData || []).filter((row: any) => !hiddenRoles.includes(row.role_name));

      if (filtered.length > 0) {
        const grouped = filtered.reduce((acc: Record<string, any>, row: any) => {
          if (!acc[row.member_name]) {
            acc[row.member_name] = {
              member_name: row.member_name,
              avatar_url: row.avatar_url,
              show_avatar: row.show_avatar,
              roles: []
            };
          }
          acc[row.member_name].roles.push(row.role_name);
          return acc;
        }, {} as Record<string, any>);
        setMembers(Object.values(grouped));
      }
      setLoading(false);
    };
    fetchTeam();
  }, [ptaId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <title>{`Meet the Team - ${ptaName}`}</title>
      
      <div className="container max-w-5xl py-8 px-4">
        <Button variant="ghost" onClick={() => navigate(`/fundraising/${ptaId}`)} className="mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to {ptaName || "Fundraising"}
        </Button>

        <div className="text-center mb-8">
          {logoUrl && (
            <img src={logoUrl} alt={ptaName} className="h-16 mx-auto mb-4 object-contain" />
          )}
          <h1 className="text-3xl font-bold mb-2 flex items-center justify-center gap-2">
            <Users className="h-7 w-7 text-primary" />
            Meet the Team
          </h1>
          <p className="text-muted-foreground">
            The dedicated volunteers making it all happen
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {members.map((member, idx) => (
            <Card key={member.member_name + idx} className="shadow-card text-center">
              <CardContent className="pt-6">
                <div className="w-20 h-20 mx-auto rounded-full bg-gradient-primary flex items-center justify-center text-primary-foreground text-2xl font-bold mb-4 overflow-hidden">
                  {member.show_avatar && member.avatar_url ? (
                    <img src={member.avatar_url} alt={member.member_name} className="w-full h-full object-cover" />
                  ) : (
                    <span>{member.member_name.split(' ').map((n: string) => n[0]).join('')}</span>
                  )}
                </div>
                <h4 className="font-semibold text-lg mb-1">{member.member_name}</h4>
                <div className="flex flex-wrap gap-1 justify-center">
                  {member.roles?.map((role: string) => (
                    <Badge key={role} variant="secondary" className="mb-1">{formatRoleLabel(role)}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {members.length === 0 && (
          <p className="text-center text-muted-foreground py-12">No team members to display.</p>
        )}
      </div>
    </div>
  );
}
